<template>
  <li class="row" :class="layoutClass" :data-qa="`chosen-accessory-${accessory.ID}`">
    <!-- Decorative: the name is right next to it, and alt would read it twice -->
    <accessory-thumb class="thumb" :src="thumbnailUrl" alt="" compact />
    <div class="title">{{ accessory.Name }}</div>
    <div v-if="vatRate !== null" class="vat">{{ vatRate }}%</div>

    <div v-if="showExclPrice" class="price price-excl" data-qa="price-excl">
      {{ money(exclPrice) }}
      <span v-if="exclEdited" class="was">{{ money(catalogExclPrice) }}</span>
    </div>
    <div v-if="showInclPrice" class="price price-incl" data-qa="price-incl">
      {{ money(inclPrice) }}
      <span v-if="inclEdited" class="was">{{ money(catalogInclPrice) }}</span>
    </div>

    <div class="buttons">
      <button
        v-if="isEditable"
        type="button"
        :title="i18n.t('/AccessoryCatalog/EditTooltip')"
        :aria-label="i18n.t('/AccessoryCatalog/EditTooltip')"
        :data-qa="`chosen-accessory-edit-${accessory.ID}`"
        @click="$emit('edit', accessory)"
      >
        <icon icon="pencil" width="20" aria-hidden="true" />
      </button>
      <button
        v-if="isEditable"
        type="button"
        :title="i18n.t('/AccessoryCatalog/RemoveTooltip')"
        :aria-label="i18n.t('/AccessoryCatalog/RemoveTooltip')"
        :data-qa="`chosen-accessory-remove-${accessory.ID}`"
        @click="$emit('remove', accessory)"
      >
        <icon icon="bin" width="20" aria-hidden="true" />
      </button>
    </div>
  </li>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import AccessoryThumb from './AccessoryThumb.vue'
import { toNumber } from '@/helpers/ponCatalog'
import currencyHelper from '@/helpers/currencyHelper'

/** Shown where there is no price to show. A fake "€ 0,00" is a wrong number. */
const NO_PRICE = '—'

/**
 * Whole-cent compare, the same rounding every other money compare in
 * ponCatalog.js does. `isPriceEdited` is the Excl-only version of this: the
 * quote carries the excl. price, so that is the one the container reasons
 * about — but this row shows both columns, and a strike belongs on the column
 * that actually changed.
 */
const differs = (quoteValue, catalogValue) => {
  const quote = toNumber(quoteValue)
  const catalog = toNumber(catalogValue)
  if (quote === null || catalog === null) return false
  return Math.round(quote * 100) !== Math.round(catalog * 100)
}

export default {
  components: {
    Icon,
    AccessoryThumb,
  },
  props: {
    /** The row as it is on the quote (name/price edits applied) */
    accessory: {
      type: Object,
      required: true,
    },
    /** The matching catalog accessory (with enrichment), null for removed/custom items */
    catalogItem: {
      type: Object,
      default: null,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    showInclPrice: {
      type: Boolean,
      default: true,
    },
    showExclPrice: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['edit', 'remove'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    /**
     * The row lays itself out (see the quote-row-grid mixin below), so which
     * price columns it has is its own business rather than something the parent
     * reaches in and sets.
     */
    layoutClass() {
      if (!this.showInclPrice) return 'row--price-excl'
      if (!this.showExclPrice) return 'row--price-incl'
      return ''
    },
    thumbnailUrl() {
      return this.catalogItem?.PonAccessoryCatalog?.ThumbnailUrl || null
    },
    /**
     * Null rather than NaN or 0 when the quote row carries no VAT rate: `NaN%`
     * is a bug on screen, and `0%` is a wrong rate presented as a fact.
     */
    vatRate() {
      return toNumber(this.accessory.Price?.BtwRate)
    },
    exclPrice() {
      return toNumber(this.accessory.Price?.Excl)
    },
    inclPrice() {
      return toNumber(this.accessory.Price?.Incl)
    },
    catalogExclPrice() {
      return toNumber(this.catalogItem?.Price?.Excl)
    },
    catalogInclPrice() {
      return toNumber(this.catalogItem?.Price?.Incl)
    },
    exclEdited() {
      return differs(this.exclPrice, this.catalogExclPrice)
    },
    inclEdited() {
      return differs(this.inclPrice, this.catalogInclPrice)
    },
  },
  methods: {
    money(value) {
      // i18n.n() console.errors on every non-finite value and formats it as a
      // zero, so an absent price has to be caught before it gets there
      return value === null ? NO_PRICE : currencyHelper.currency(value)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/quote-row-grid';

.row {
  @include quote-row-grid(thumb title vat price-excl price-incl buttons);

  &.row--price-incl {
    @include quote-row-grid(thumb title vat price-incl buttons);
  }

  &.row--price-excl {
    @include quote-row-grid(thumb title vat price-excl buttons);
  }
}

.thumb {
  grid-area: thumb;
  height: 2.25em;
}

.was {
  display: block;
  color: $gray-dark;
  text-decoration: line-through;
}

button {
  margin-top: 0.1em;
}

button + button {
  margin-left: 0.5em;
}
</style>
